/* ─────────────────────────────────────────────────────────────
   Validation Adapter — Compiles ResolvedValidationConfig into
   a validator function compatible with TanStack Form.
   ───────────────────────────────────────────────────────────── */

import type { ResolvedValidationConfig } from '../config/types';

export type ValidatorFn = (value: unknown, getFieldValue?: (field: string) => unknown) => string | undefined;

type CustomRuleFn = (value: unknown, args?: Record<string, unknown>, getFieldValue?: (field: string) => unknown) => string | undefined;

const customRuleRegistry = new Map<string, CustomRuleFn>();

export function registerCustomRule(
  rule: string,
  fn: CustomRuleFn,
) {
  customRuleRegistry.set(rule, fn);
}

export function compileValidator(config: ResolvedValidationConfig, label?: string): ValidatorFn {
  const fieldLabel = label ?? 'This field';

  return (value: unknown, getFieldValue?: (field: string) => unknown): string | undefined => {
    const strValue = typeof value === 'string' ? value : value != null ? String(value) : '';
    const isEmpty = value == null || strValue.trim() === '';

    // Required check
    if (config.required && isEmpty) {
      return `${fieldLabel} is required`;
    }

    // Skip further checks if empty and not required
    if (isEmpty) return undefined;

    // String length checks
    if (config.minLength != null && strValue.length < config.minLength) {
      return `${fieldLabel} must be at least ${config.minLength} characters`;
    }
    if (config.maxLength != null && strValue.length > config.maxLength) {
      return `${fieldLabel} must be at most ${config.maxLength} characters`;
    }

    // Numeric range checks
    if (config.min != null || config.max != null) {
      const numValue = typeof value === 'number' ? value : parseFloat(strValue);
      if (!isNaN(numValue)) {
        if (config.min != null && numValue < config.min) {
          return `${fieldLabel} must be at least ${config.min}`;
        }
        if (config.max != null && numValue > config.max) {
          return `${fieldLabel} must be at most ${config.max}`;
        }
      }
    }

    // Pattern check
    if (config.pattern) {
      const regex = new RegExp(config.pattern);
      if (!regex.test(strValue)) {
        return config.patternMessage ?? `${fieldLabel} format is invalid`;
      }
    }

    // Custom rules
    if (config.custom) {
      for (const { rule, args } of config.custom) {
        const ruleFn = customRuleRegistry.get(rule);
        if (ruleFn) {
          const error = ruleFn(value, args, getFieldValue);
          if (error) return error;
        }
      }
    }

    return undefined;
  };
}
